import { useMutation } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import xor from 'lodash/xor';
import { useState } from 'react';
import styled from 'styled-components';

import { color, dimension } from '@creditornot/cb-ingredients/design-tokens';
import {
  ListingRecommendationsApproveRequest,
  ListingRecommendationsBulkApproveRequest,
  ListingRecommendationsBulkRejectRequest,
  ListingRecommendationsRejectRequest,
  ListingReason as ListingReasonType,
  ListingRecommendationsBulkActionFilter,
} from 'common/api-types/recommendations';
import { ContentContainer } from 'common/components/cookbook/ContentContainer';
import { SelectionState } from 'common/components/cookbook/DataTable/types';
import { SortButton } from 'common/components/cookbook/SortButton';
import { Stack } from 'common/components/cookbook/Stack';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useSetSearch } from 'common/hooks/useSetSearch';
import { useApiQueryClient } from 'common/query/useApiQueryClient';
import { AxiosListingRecommendationsError, LocalizedMessageId } from 'common/types';
import { downloadBlob } from 'common/utils/downloadUtils';
import {
  selectionRecommendationsRouteApi,
  SelectionRecommendationsSearchRouteSchemaType,
} from 'features/selection-recommendations';
import {
  approveListingRecommendations,
  bulkApproveListingRecommendations,
  bulkRejectListingRecommendations,
  rejectListingRecommendations,
} from 'features/selection-recommendations/api';
import { FilterSelect } from 'features/selection-recommendations/common/FilterSelect/FilterSelect';
import { ActionSelect } from 'features/selection-recommendations/common/RecommendationActions/ActionSelect';
import { ApproveRecommendationModal } from 'features/selection-recommendations/common/RecommendationActions/DownloadApproved/ApproveRecommendationModal';
import { RecommendationAction } from 'features/selection-recommendations/types';

import { ListingReasons } from './ListingReasons';
import { ListingRecommendationsList } from './ListingRecommendationsList';
import {
  LISTING_RECOMMENDATIONS_QUERY_KEY_ROOT,
  useListingRecommendationsInfiniteQuery,
} from '../../query';
import { selectedProductFamilyIdsAtom, selectedRecommendationsAtom } from '../state';
import { RejectListingForm } from './RejectRecommendations/RejectListingForm';
import { sortParamMap } from './sortParamMap';
import { ListingRecommendationRejectionFormValues, ListingRecommendationsSortType } from './types';
import { getRecommendationItems } from './utils';

const Header = styled(ContentContainer)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${dimension.spaceRegularXlarge};
  overflow: auto;
`;

const HorizontalDivider = styled.div`
  height: 20px;
  border-left: 2px solid ${color.border};
`;

export const ListingRecommendations = () => {
  const f = useFormatMessage();
  const [countryCode] = useSelectedCountryCodes();
  const [selectedState, setSelectedState] = useAtom(selectedRecommendationsAtom);
  const { bulkSelect, unselected, selected } = selectedState;
  const [action, setAction] = useState<RecommendationAction | null>(null);
  const queryClient = useApiQueryClient();
  const setSearch = useSetSearch<SelectionRecommendationsSearchRouteSchemaType>();
  const selectedProductFamilyIds = useAtomValue(selectedProductFamilyIdsAtom);

  const { listingSortBy = 'EXPECTED_BUSINESS_IMPACT_DESC', listingReasons = [] } =
    selectionRecommendationsRouteApi.useSearch();

  const sortParams = sortParamMap[listingSortBy];
  const sortingDirection = sortParams.sortingDirection;

  const {
    listingRecommendations,
    error,
    isFetching,
    totalCount,
    data,
    isLoading,
    hasNextPage,
    fetchNextPage,
  } = useListingRecommendationsInfiniteQuery(countryCode, {
    listingRecommendationsSortBy: sortParams.sortBy,
    sortingDirection,
    pageSize: 100,
    filter: {
      productFamilyIds: selectedProductFamilyIds,
      reasons: listingReasons,
    },
  });

  const onDownloadSuccess = async (blob: Blob) => {
    const fileName = 'listing_recommendations.csv';
    downloadBlob(new Blob([blob]), fileName);
    await invalidateQuery();
  };

  const resetSelectionState = () => {
    setSelectedState({ bulkSelect: false, selected: [], unselected: [] });
    setAction(null);
  };

  const invalidateQuery = async () => {
    resetSelectionState();
    await queryClient.invalidateQueries({
      queryKey: [LISTING_RECOMMENDATIONS_QUERY_KEY_ROOT],
    });
  };

  const {
    mutate: approveMutate,
    isPending: approveIsPending,
    error: approveError,
  } = useMutation<Blob, AxiosListingRecommendationsError, ListingRecommendationsApproveRequest>({
    mutationFn: approveListingRecommendations,
    onSuccess: onDownloadSuccess,
  });

  const {
    mutate: bulkApproveMutate,
    isPending: bulkApproveIsPending,
    error: bulkApproveError,
  } = useMutation<Blob, AxiosListingRecommendationsError, ListingRecommendationsBulkApproveRequest>(
    {
      mutationFn: bulkApproveListingRecommendations,
      onSuccess: onDownloadSuccess,
    },
  );

  const onRowSelectChange = (value: 'ALL' | SelectionState) => {
    setSelectedState(value === 'ALL' ? { bulkSelect: true, selected: [], unselected: [] } : value);
  };

  const selectedIds = selected.map((current) => current.rowId ?? []);

  const selectedRecommendationCount = bulkSelect
    ? totalCount - unselected.length
    : selectedIds.length;

  const requestedAt = data?.pages[0]?.meta.requestedAt;

  const handleDownload = () => {
    if (!requestedAt) {
      return;
    }

    if (!bulkSelect) {
      const recommendationItems = getRecommendationItems(selectedIds, listingRecommendations);

      approveMutate({
        recommendations: recommendationItems,
        countryCode,
      });

      return;
    }

    const unselectedRecommendationItems = getRecommendationItems(
      unselected,
      listingRecommendations,
    );

    const bulkActionFilter: ListingRecommendationsBulkActionFilter = {
      countryCode,
      excludedRecommendationItems: unselectedRecommendationItems,
      productFamilyIds: selectedProductFamilyIds,
      reasons: listingReasons,
    };

    bulkApproveMutate({
      filter: bulkActionFilter,
      meta: {
        expectedActionsCount: selectedRecommendationCount,
        recommendationsRequestedAt: requestedAt,
      },
    });
  };

  const {
    mutate: rejectMutate,
    isPending: rejectIsPending,
    error: rejectError,
  } = useMutation<Blob, AxiosListingRecommendationsError, ListingRecommendationsRejectRequest>({
    mutationFn: rejectListingRecommendations,
    onSuccess: invalidateQuery,
  });

  const {
    mutate: bulkRejectMutate,
    isPending: bulkRejectIsPending,
    error: bulkRejectError,
  } = useMutation<Blob, AxiosListingRecommendationsError, ListingRecommendationsBulkRejectRequest>({
    mutationFn: bulkRejectListingRecommendations,
    onSuccess: invalidateQuery,
  });

  const handleReject = (values: ListingRecommendationRejectionFormValues) => {
    if (!requestedAt) {
      return;
    }

    if (!bulkSelect) {
      const selectedRecommendationItems = getRecommendationItems(
        selectedIds,
        listingRecommendations,
      );

      rejectMutate({
        recommendations: selectedRecommendationItems,
        countryCode,
        reason: values.reason,
        comment: values.comment,
      });

      return;
    }

    const unselectedRecommendationItems = getRecommendationItems(
      unselected,
      listingRecommendations,
    );

    const bulkActionFilter: ListingRecommendationsBulkActionFilter = {
      countryCode,
      excludedRecommendationItems: unselectedRecommendationItems,
      productFamilyIds: selectedProductFamilyIds,
      reasons: listingReasons,
    };

    bulkRejectMutate({
      filter: bulkActionFilter,
      meta: {
        expectedActionsCount: selectedRecommendationCount,
        recommendationsRequestedAt: requestedAt,
      },
      reason: values.reason,
      comment: values.comment,
    });
  };

  const approveListingRecommendationError = approveError || bulkApproveError;
  const rejectListingRecommendationError = rejectError || bulkRejectError;

  const handleSortBy = async (sortBy: ListingRecommendationsSortType) => {
    await resetSelectionState();
    setSearch({ listingSortBy: sortBy });
  };

  const options = Object.entries(sortParamMap).map(([key]) => ({
    label: f(`selection_recommendations.sort_by:${key}` as LocalizedMessageId),
    sortBy: key as ListingRecommendationsSortType,
    sortingType: sortingDirection,
  }));

  const handleChangeReason = (reason: ListingReasonType) => {
    setSearch({ listingReasons: xor(listingReasons, [reason]) });
  };

  return (
    <>
      <Header borderBottom={true} verticalPadding="sm">
        <Stack direction="row" gap="regularXlarge" alignItems="center">
          <SortButton
            sortBy={listingSortBy}
            options={options}
            onSortChange={handleSortBy}
            sortingType={sortingDirection}
          />
          <HorizontalDivider />
        </Stack>
        <FilterSelect
          resetSelectionState={resetSelectionState}
          filters={['reason']}
          reasonFilter={
            <ListingReasons onReasonChange={handleChangeReason} selectedReasons={listingReasons} />
          }
        />
      </Header>
      <ListingRecommendationsList
        error={error}
        groupByCategory={listingRecommendations}
        isFetching={isFetching}
        rowSelection={{
          onRowSelectChange,
          selectedRows: bulkSelect && !unselected.length ? 'ALL' : selectedIds,
        }}
        isLoading={isLoading}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        totalCount={totalCount}
      />
      <ActionSelect
        isVisible={!!selectedRecommendationCount}
        handleRejectionModalOpen={() => {
          setAction('REJECT_RECOMMENDATIONS');
        }}
        handleApprovalModalOpen={() => {
          setAction('APPROVE_RECOMMENDATIONS');
        }}
      />
      <RejectListingForm
        isOpen={action === 'REJECT_RECOMMENDATIONS'}
        onDismiss={() => setAction(null)}
        onReject={handleReject}
        rejectIsPending={rejectIsPending || bulkRejectIsPending}
        selectedRecommendationCount={selectedRecommendationCount}
        error={rejectListingRecommendationError}
        canDownload={false}
      />
      <ApproveRecommendationModal
        canDownload={false}
        isOpen={action === 'APPROVE_RECOMMENDATIONS'}
        onDismiss={() => setAction(null)}
        isDownloading={approveIsPending || bulkApproveIsPending}
        onDownload={handleDownload}
        error={approveListingRecommendationError}
        description={f('selection_recommendations.approve_listing_recommendation_description', {
          count: selectedRecommendationCount,
        })}
      />
    </>
  );
};
